import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {Badge, Box, Checkbox, Group, Loader, SimpleGrid, Text, Textarea, Title, Button, MantineColor, Tooltip} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';
import ScTextControl from '@/components/sc-controls/form-controls/v2/sc-text-control';
import SCDropdownList from '@/components/sc-controls/form-controls/sc-dropdownlist';
import * as Enums from '@/utils/enums';
import Helper from '@/utils/helper';
import CustomerService from '@/services/customer/customer-service';
import Fetch from '@/utils/Fetch';
import PS from '@/services/permission/permission-service';
import { showNotification } from '@mantine/notifications';
import SCModal from '@/PageComponents/Modal/SCModal';
import {LocationDTO} from "@/interfaces/api/models";
import LocationLatLngControl from '@/PageComponents/customer/LocationLatLngControl';
import LocationDeactivateModal from '@/PageComponents/customer/LocationDeactivateModal';
import LocationPrimaryReplacementModal from '@/PageComponents/customer/LocationPrimaryReplacementModal';

// Combined props: manage-location modal interface + child-embedded interface
// Modal replacement props (from components/modals/location/manage-location.js):
// { isNew, location, module, moduleData, countries, onSave, onCancel, accessStatus, backButtonText }
// Embedded/child form props (pattern used in components/customer/LocationForm.tsx):
// { triggerFormSaveCounter, onSaveValues, onDirtyStateChange, onErrors }

type Props = {
  // manage-location interface
  isNew?: boolean;
  location?: LocationDTO;
  module?: any;
  moduleData?: any;
  countries?: any[];
  // related: the other active locations as reloaded after the save (their RowVersions can change too)
  onSave?: (saved: any, related?: LocationDTO[]) => void;
  onCancel?: () => void;
  accessStatus?: any;
  backButtonText?: string;
  // child/embedded interface
  triggerFormSaveCounter?: number;
  onSaveValues?: (values: any) => void;
  onDirtyStateChange?: (dirty: boolean) => void;
  onErrors?: (errors: any) => void;
  // control whether to show modal chrome; default: infer from presence of onSave (modal)
  embedded?: boolean;
  defaultValues?: Partial<DefaultValues>;
};

type DefaultValues ={
    primaryToggle: {
        toggled?: boolean | ((locationType:any) => boolean),
        disabled?: boolean
        alwaysChecked?: boolean | ((locationType: any) => boolean),
        color?: MantineColor | ((locationType: any) => MantineColor|undefined),
        tooltipLabel?: string,
        primaryName?: string,
    }
}

const editCustomerPermission = PS.hasPermission(Enums.PermissionName.EditCustomer);

function LocationForm(props: Props) {
  const {
    isNew = true,
    location,
    module,
    moduleData,
    countries: countriesProp,
    onSave,
    onCancel,
    accessStatus,
    backButtonText,
    triggerFormSaveCounter,
    onSaveValues,
    onDirtyStateChange,
    onErrors,
    embedded,
    defaultValues
  } = props; 

    let defaultPrimaryName = defaultValues?.primaryToggle?.primaryName ? defaultValues.primaryToggle.primaryName : '';
  
  // Determine rendering mode: modal (replacement for manage-location) vs embedded child form
  const isModalMode = useMemo(() => {
    if (typeof embedded === 'boolean') return !embedded;
    // Heuristic: if onSave handler (from modal usage) exists, prefer modal behavior
    return typeof onSave === 'function';
  }, [embedded, onSave]);

  // Initial values: support both legacy manage-location naming (Description) and newer (LocationName)
  const initialValues = useMemo(() => {
    const name = location?.Description ?? defaultPrimaryName;
    const typeString = location?.LocationType != null ? Enums.getEnumStringValue(Enums.LocationType, location.LocationType) || 'Delivery' : 'Delivery';
    return {
      // Keep both keys to ease reuse (form-only convenience)
      Description: name,
      LocationName: name,
      AddressLine1: location?.AddressLine1 ?? '',
      AddressLine2: location?.AddressLine2 ?? '',
      AddressLine3: location?.AddressLine3 ?? '',
      AddressLine4: location?.AddressLine4 ?? '',
      AddressLine5: location?.AddressLine5 ?? '',
      Latitude: location?.Latitude ?? null,
      Longitude: location?.Longitude ?? null,
      // Type is string representation; LocationType is numeric enum used for saves
      Type: typeString,
      LocationType: location?.LocationType ?? (Enums.LocationType as any)[typeString] ?? Enums.LocationType.Delivery,
      Country: null,
      CountryID: location?.CountryID ?? null,
      CountryDescription: location?.CountryDescription ?? null,
      IsPrimary: location?.IsPrimary ?? false,
      IsActive: location?.IsActive ?? true,
      ID: location?.ID,
      RowVersion: location?.RowVersion,
    };
  }, [location,defaultPrimaryName]);

  const form = useForm({
    initialValues,
    validate: {
      Description: (x: any) =>
        Helper.validateInputStringOut({ value: x, controlType: Enums.ControlType.Text, required: true, customErrorText: 'Please specify Location Name' } as any),
      AddressLine1: (x: any) => {
        const baseError = Helper.validateInputStringOut({ value: x, controlType: Enums.ControlType.Text, required: true, customErrorText: 'Please specify Address Line 1' } as any) as any;
        if (baseError) return baseError as any;
        if ((x ?? '').length > 99) return 'Address Line 1 must be less than 100 characters';
        return null;
      },
      AddressLine2: (x: any) => {
        if ((x ?? '').length > 99) return 'Address Line 2 must be less than 100 characters';
        return null;
      },
      AddressLine3: (x: any) => {
        if ((x ?? '').length > 99) return 'Address Line 3 must be less than 100 characters';
        return null;
      },
      AddressLine4: (x: any) => {
        if ((x ?? '').length > 99) return 'Address Line 4 must be less than 100 characters';
        return null;
      },
      AddressLine5: (x: any) => {
        if ((x ?? '').length > 99) return 'Address Line 5 must be less than 100 characters';
        return null;
      },
      Type: (x: any) =>
        Helper.validateInputStringOut({ value: x, controlType: Enums.ControlType.Text, required: true, customErrorText: 'Please specify Location Type' } as any),
      Country: (x: any) =>
        Helper.validateInputStringOut({ value: x, controlType: Enums.ControlType.Text, required: true, customErrorText: 'Please specify Country' } as any),
    },
  });

  const [useCoords, setUseCoords] = useState<boolean>(() => (initialValues.Latitude != null && initialValues.Longitude != null));

  // Keep Description and LocationName in sync for cross-compatibility
  const syncNameFields = useCallback(
    (newName: string) => {
      if (form.values.LocationName !== newName) form.setFieldValue('LocationName', newName);
      if (form.values.Description !== newName) form.setFieldValue('Description', newName);
    },
    [form]
  );

  // Debounced form values for dirty calculation to avoid chatter
  const [debounced] = useDebouncedValue(form.values, 150);

  // Initial address textarea text: derive from initial values without trailing empty lines
  const initialAddressText = useMemo(() => {
    const lines = [
      initialValues.AddressLine1 ?? '',
      initialValues.AddressLine2 ?? '',
      initialValues.AddressLine3 ?? '',
      initialValues.AddressLine4 ?? '',
      initialValues.AddressLine5 ?? '',
    ];
    let lastNonEmpty = -1;
    for (let i = 0; i < lines.length; i++) {
      if ((lines[i] ?? '').length > 0) lastNonEmpty = i;
    }
    if (lastNonEmpty === -1) return '';
    return lines.slice(0, lastNonEmpty + 1).join('\n');
  }, [initialValues]);

  const [addressText, setAddressText] = useState<string>(initialAddressText);

  useEffect(() => {
    if (onDirtyStateChange) {
      const dirtyItems = Object.entries(form.values).filter(([key, value]: any) =>
        typeof value === 'string'
          ? (value || '').trim() !== (initialValues as any)[key]
          : typeof value === 'object'
          ? (value as any)?.ID !== (initialValues as any)[key]?.ID
          : value !== (initialValues as any)[key]
      );
      onDirtyStateChange(dirtyItems.length !== 0 && form.isDirty());
    }
  }, [initialValues, debounced, onDirtyStateChange, form]);

  // Countries
  const [countries, setCountries] = useState<any[]>(countriesProp ?? []);
  useEffect(() => {
    let mounted = true;
    const loadCountries = async () => {
      if (countriesProp && countriesProp.length) {
        setCountries(countriesProp);
        // Default selection logic will run below
      } else {
        const response = await CustomerService.getCountries();
        if (mounted && response) setCountries(response);
      }
    };
    loadCountries();
    return () => {
      mounted = false;
    };
  }, [countriesProp]);

  // Default country selection: prefer existing CountryID, else South Africa
  useEffect(() => {
    if (countries?.length) {
      if (!form.values.Country) {
        const existingId = initialValues.CountryID;
        if (existingId) {
          const match = countries.find((x: any) => x.ID === existingId);
          if (match) {
            form.setFieldValue('Country', match);
            form.setFieldValue('CountryID', match.ID);
            form.setFieldValue('CountryDescription', match.Description);
            return;
          }
        }
        const sa = countries.find((x: any) => x.Description === 'South Africa');
        if (sa) {
          form.setFieldValue('Country', sa);
          form.setFieldValue('CountryID', sa.ID);
          form.setFieldValue('CountryDescription', sa.Description);
        }
      }
    }
  }, [countries, form, initialValues.CountryID]);

  // External trigger (embedded use) to submit and bubble values
  const [saveCount, setSaveCount] = useState(0);
  useEffect(() => {
    if (triggerFormSaveCounter === undefined) return;
    if (saveCount !== 0) {
      handleEmbeddedSubmit(form.values);
    }
    setSaveCount((p: any) => p + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerFormSaveCounter]);

  const normalizePayload = (values: any) => {
    const locTypeNumber = (Enums.LocationType as any)[values.Type] ?? values.LocationType;
    const country = values.Country;
    return {
      ...values,
      Description: values.Description ?? values.LocationName ?? '',
      LocationName: values.LocationName ?? values.Description ?? '',
      Type: values.Type,
      LocationType: locTypeNumber,
      Country: country,
      CountryID: country?.ID ?? values.CountryID ?? null,
      CountryDescription: country?.Description ?? values.CountryDescription ?? null,
    };
  };

  const handleEmbeddedSubmit = (values: any) => {
    if (form.validate() && form.isValid()) {
      const payload = normalizePayload(values);
      onSaveValues && onSaveValues(payload);
    } else {
      onErrors && onErrors(form.errors);
    }
  };

  // Modal save (API persist) when used as manage-location replacement
  const [saving, setSaving] = useState(false);
  const canEdit = editCustomerPermission || module === Enums.Module.Supplier;
  const isPrimaryAlreadySet = useMemo(() => (!isNew && !!location?.IsPrimary) || false, [isNew, location]);

  const toLocationParams = (values: any) => {
    const paramsBase: any = {
      ID: values.ID,
      RowVersion: values.RowVersion,
      Description: values.Description,
      AddressLine1: values.AddressLine1,
      AddressLine2: values.AddressLine2,
      AddressLine3: values.AddressLine3,
      AddressLine4: values.AddressLine4,
      AddressLine5: values.AddressLine5,
      Latitude: values.Latitude ?? null,
      Longitude: values.Longitude ?? null,
      IsActive: values.IsActive,
      IsPrimary: values.IsPrimary,
      CountryID: values.CountryID,
      LocationType: values.LocationType,
    };

    return module === Enums.Module.Customer
      ? { ...paramsBase, CustomerID: moduleData?.ID }
      : { ...paramsBase, SupplierID: moduleData?.ID };
  };

  const saveLocation = async () => {
    setSaving(true);
    const ok = form.validate() && form.isValid();
    if (!ok) {
      showNotification({ message: 'There are errors on the page', color: 'yellow.7', autoClose: 4000, withCloseButton: true });
      setSaving(false);
      return;
    }

    const params = toLocationParams(normalizePayload(form.values));

    try {
      const response = isNew
        ? await Fetch.post({ url: '/Location', params })
        : await Fetch.put({ url: '/Location', params });

      if (response?.ID) {
        if (await promoteReplacement()) {
          showNotification({ title: 'Saved', message: 'Location saved successfully', color: 'scBlue', autoClose: 3000, withCloseButton: true });
        }
        await finishSave(response);
      } else {
        showNotification({ message: 'Location failed to save', color: 'yellow.7', autoClose: 4000, withCloseButton: true });
        setSaving(false);
      }
    } catch (e) {
      showNotification({ message: 'Location failed to save', color: 'yellow.7', autoClose: 4000, withCloseButton: true });
      setSaving(false);
    }

    if (!isNew) setSaving(false);
  };

  // Deactivate / activate an existing location, closing the modal via onSave.
  // Deactivate persists the location as it was loaded (unsaved edits are discarded, with a warning);
  // activate saves the form as it stands, like Save.
  const canChangeActiveStatus = canEdit && !isNew && !!location?.ID;
  const isAccessLocked = accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess;
  const locationTypeName = Enums.getEnumStringValue(Enums.LocationType, location?.LocationType) || 'Delivery';
  const [changingActiveStatus, setChangingActiveStatus] = useState(false);
  const [deactivateCandidates, setDeactivateCandidates] = useState<LocationDTO[] | null>(null);

  // Compared field by field: form.isDirty() is always true here because the country is set after mount
  const hasUnsavedChanges = useMemo(() => {
    if (!location) return false;
    const v = form.values;
    const same = (a: any, b: any) => (a ?? '') === (b ?? '');
    return !same(v.Description, location.Description)
      || !same(v.AddressLine1, location.AddressLine1) || !same(v.AddressLine2, location.AddressLine2)
      || !same(v.AddressLine3, location.AddressLine3) || !same(v.AddressLine4, location.AddressLine4)
      || !same(v.AddressLine5, location.AddressLine5)
      || v.LocationType !== location.LocationType
      || (!!location.CountryID && v.CountryID !== location.CountryID)
      || !same(v.Latitude, location.Latitude) || !same(v.Longitude, location.Longitude)
      || !!v.IsPrimary !== !!location.IsPrimary;
  }, [form.values, location]);

  const getActiveLocations = async (): Promise<LocationDTO[]> => {
    const ownerParam = module === Enums.Module.Customer ? { customerID: moduleData?.ID } : { supplierID: moduleData?.ID };
    const response = await Fetch.post({
      url: '/Location/GetLocations',
      params: { pageIndex: 0, pageSize: 1000, searchPhrase: '', sortExpression: '', sortDirection: '', isActive: true, ...ownerParam },
    } as any);
    if (!response?.Results) throw new Error('Could not load locations');
    return (response.Results as LocationDTO[]).filter((x) => x.IsActive);
  };

  const getOtherActiveLocationsOfType = async (locationType: number): Promise<LocationDTO[]> =>
    (await getActiveLocations()).filter((x) => x.ID !== location?.ID && x.LocationType === locationType);

  const putLocation = async (values: any) => {
    const response = await Fetch.put({ url: '/Location', params: toLocationParams(values) } as any);
    if (!response?.ID) throw new Error('Location failed to save');
    return response;
  };

  // Saving can change other locations server side (a new primary demotes the previous one of its type),
  // which gives them a new RowVersion. Reload the active locations so the caller can replace its copies:
  // a stale RowVersion fails the API's concurrency check the next time that location is saved.
  // onSave receives (saved location, the other active locations as they are now).
  const finishSave = async (saved: any) => {
    if (!moduleData?.ID) {
      onSave && onSave(saved, []);
      return;
    }
    try {
      const latest = await getActiveLocations();
      onSave && onSave(latest.find((x) => x.ID === saved.ID) ?? saved, latest.filter((x) => x.ID !== saved.ID));
    } catch (e) {
      onSave && onSave(saved, []);
    }
  };

  // ===== Primary per location type =====
  // Each type in use has one primary. The rules need the owner's other locations, so they apply in the
  // modal when the owner exists; otherwise (e.g. the embedded first location of a new customer) the
  // caller's primaryToggle defaults apply.
  //  - The only location of its type is its primary: the box is ticked and locked.
  //  - The current primary can untick its box, or change its type, once another location of its type
  //    exists: the user picks which location becomes primary instead, made primary after this one is saved.
  //  - Changing the type makes the location primary when it is the only one of the new type, and
  //    non-primary otherwise (that type already has a primary).
  const [otherLocations, setOtherLocations] = useState<LocationDTO[] | null>(null);
  const [otherLocationsFailed, setOtherLocationsFailed] = useState(false);
  useEffect(() => {
    if (!isModalMode || !moduleData?.ID) return;
    getActiveLocations()
      .then((all) => setOtherLocations(all.filter((x) => x.ID !== location?.ID)))
      .catch(() => setOtherLocationsFailed(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const typeRulesApply = isModalMode && !!moduleData?.ID && !otherLocationsFailed;
  const typeRulesLoading = typeRulesApply && otherLocations === null;
  const othersOfType = (type: number) => (otherLocations ?? []).filter((x) => x.LocationType === type);
  const loadedType = location?.LocationType;
  const loadedPrimary = !isNew && !!location?.IsPrimary;
  const loadedTypeName = (Enums.getEnumStringValue(Enums.LocationType, loadedType) || 'Delivery').toLowerCase();
  const currentType = form.values.LocationType as number;
  const isOnlyOfType = typeRulesApply && !typeRulesLoading && othersOfType(currentType).length === 0;
  // The location the user picked to become primary of the loaded type instead of this one
  const [replacement, setReplacement] = useState<LocationDTO | null>(null);
  const holdsPrimary = loadedPrimary && currentType === loadedType && !replacement;
  const [replacing, setReplacing] = useState<null | { typeValue?: string }>(null);

  useEffect(() => {
    if (!typeRulesApply || typeRulesLoading) return;
    const primary = isOnlyOfType || (currentType === loadedType && loadedPrimary && !replacement);
    if (!!form.values.IsPrimary !== primary) form.setFieldValue('IsPrimary', primary);
    // Only when the list loads or the type changes: the user's own ticks stay as they are
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeRulesLoading, currentType]);

  const applyType = (val: any) => {
    form.setFieldValue('Type', val);
    form.setFieldValue('LocationType', (Enums.LocationType as any)[val]);
  };

  const changeType = (val: any) => {
    const newType = (Enums.LocationType as any)[val];
    if (typeRulesApply && holdsPrimary && newType !== loadedType && othersOfType(loadedType as number).length > 0) {
      setReplacing({ typeValue: val });
      return;
    }
    // Back to its own type: it stays that type's primary
    if (newType === loadedType) setReplacement(null);
    applyType(val);
  };

  const togglePrimary = (checked: boolean) => {
    if (!checked && holdsPrimary) {
      setReplacing({});
      return;
    }
    // Ticking it again in its own type keeps the role, so drop the replacement picked for it
    if (checked && replacement && currentType === loadedType) setReplacement(null);
    form.setFieldValue('IsPrimary', checked);
    if (checked) form.setFieldValue('IsActive', true);
  };

  const confirmReplacement = (picked: LocationDTO) => {
    setReplacement(picked);
    if (replacing?.typeValue) applyType(replacing.typeValue);
    else form.setFieldValue('IsPrimary', false);
    setReplacing(null);
  };

  // Makes the picked location primary of the loaded type, after this location was saved
  const promoteReplacement = async () => {
    if (!replacement) return true;
    try {
      const latest = (await getOtherActiveLocationsOfType(loadedType as number)).find((x) => x.ID === replacement.ID) ?? replacement;
      await putLocation({ ...latest, IsActive: true, IsPrimary: true });
      return true;
    } catch (e) {
      showNotification({
        message: `Location saved, but ${replacement.Description} could not be set as the primary ${loadedTypeName} address`,
        color: 'yellow.7', autoClose: 6000, withCloseButton: true,
      });
      return false;
    }
  };

  const startDeactivate = async () => {
    setChangingActiveStatus(true);
    try {
      setDeactivateCandidates(location?.IsPrimary ? await getOtherActiveLocationsOfType(location.LocationType) : []);
    } catch (e) {
      showNotification({ message: 'Could not load locations', color: 'yellow.7', autoClose: 4000, withCloseButton: true });
    }
    setChangingActiveStatus(false);
  };

  const confirmDeactivate = async (newPrimary: LocationDTO | null) => {
    setChangingActiveStatus(true);
    try {
      // Deactivate first: promoting the new primary demotes this location server side,
      // which would leave this location's RowVersion stale for a second save.
      const response = await putLocation({ ...location, IsActive: false, IsPrimary: false });
      if (newPrimary) {
        try {
          await putLocation({ ...newPrimary, IsActive: true, IsPrimary: true });
        } catch (e) {
          showNotification({
            message: `Location deactivated, but ${newPrimary.Description} could not be set as primary`,
            color: 'yellow.7', autoClose: 6000, withCloseButton: true,
          });
          await finishSave(response);
          return;
        }
      }
      showNotification({ title: 'Saved', message: 'Location deactivated', color: 'scBlue', autoClose: 3000, withCloseButton: true });
      setDeactivateCandidates(null);
      await finishSave(response);
    } catch (e) {
      showNotification({ message: 'Location failed to deactivate', color: 'yellow.7', autoClose: 4000, withCloseButton: true });
      setChangingActiveStatus(false);
    }
  };

  const activate = async () => {
    if (!(form.validate() && form.isValid())) {
      showNotification({ message: 'There are errors on the page', color: 'yellow.7', autoClose: 4000, withCloseButton: true });
      return;
    }
    setChangingActiveStatus(true);
    try {
      const values = normalizePayload(form.values);
      // Primary when the switch shows it, or when its type has no active primary so each type in use keeps one
      const primaryToggled = (!typeRulesApply && (alwaysChecked || defaultToggleValue)) || !!values.IsPrimary;
      const others = await getOtherActiveLocationsOfType(values.LocationType);
      const response = await putLocation({ ...values, IsActive: true, IsPrimary: primaryToggled || !others.some((x) => x.IsPrimary) });
      showNotification({ title: 'Saved', message: 'Location activated', color: 'scBlue', autoClose: 3000, withCloseButton: true });
      await finishSave(response);
    } catch (e) {
      showNotification({ message: 'Location failed to activate', color: 'yellow.7', autoClose: 4000, withCloseButton: true });
      setChangingActiveStatus(false);
    }
  };

    const [alwaysChecked, setAlwaysChecked] = useState<boolean>(false)
    const [defaultToggleValue, setDefaultToggleState] = useState<boolean>(false)
    const [defaultPrimaryToggleColor, setDefaultPrimaryToggleColor] = useState<MantineColor | undefined>(undefined)

    useEffect(() => {
        if (typeof defaultValues?.primaryToggle?.alwaysChecked === 'function') {
            setAlwaysChecked(defaultValues.primaryToggle.alwaysChecked(form.values.Type))
        }
        else if (typeof defaultValues?.primaryToggle?.alwaysChecked === 'boolean') {
            setAlwaysChecked(defaultValues.primaryToggle.alwaysChecked)
        }
        if (typeof defaultValues?.primaryToggle?.toggled === 'function') {
            setDefaultToggleState(defaultValues.primaryToggle.toggled(form.values.Type))
        }
        else if (typeof defaultValues?.primaryToggle?.toggled === 'boolean') {
            setDefaultToggleState(defaultValues.primaryToggle.toggled)
        }
        if (typeof defaultValues?.primaryToggle?.color === 'function') {
            setDefaultPrimaryToggleColor(defaultValues.primaryToggle.color(form.values.Type))
        }
        else if (defaultValues?.primaryToggle?.color) {
            setDefaultPrimaryToggleColor(defaultValues.primaryToggle.color)
        }

    }, [form.values.Type])


  // Design: the role (primary) sits at the bottom of the form and is summarised as a status pill in the header.
  const typeName = (form.values.Type || 'Delivery').toLowerCase();
  const isInactive = !isNew && location?.IsActive === false;
  // With the type rules the box shows IsPrimary as the rules set it; otherwise the caller's defaults apply
  const primaryChecked = typeRulesApply
    ? !!form.values.IsPrimary
    : alwaysChecked || defaultToggleValue || !!form.values.IsPrimary;
  const primaryDisabled = !canEdit || defaultValues?.primaryToggle?.disabled || (typeRulesApply
    ? typeRulesLoading || isOnlyOfType
    : !isNew && isPrimaryAlreadySet);
  const primaryTooltip = typeRulesApply
    ? (isOnlyOfType ? `This is the only ${typeName} address, so it is the primary ${typeName} address.` : undefined)
    : (!isNew && isPrimaryAlreadySet)
      ? `This is the primary ${typeName} address. To change it, set another ${typeName} address as primary.`
      : defaultValues?.primaryToggle?.tooltipLabel;

  const statusPill = isInactive
    ? <Badge variant={'light'} color={'gray'} radius={'xl'} tt={'none'} fw={600}>Inactive</Badge>
    : primaryChecked
      ? <Badge variant={'light'} color={'scBlue'} radius={'xl'} tt={'none'} fw={600}>Primary {typeName}</Badge>
      : null;

  // UI form
  const formContent = (
    <Box style={{ maxWidth: 1020 }}>
      <SimpleGrid
        spacing={'sm'}
        verticalSpacing={0}
        cols={{ base: 1, sm: 2 }}
      >
        <ScTextControl
          label={'Name'}
          placeholder={'e.g. Head office, Site B'}
          {...form.getInputProps('Description')}
          onChange={(e: any) => syncNameFields(e?.currentTarget?.value ?? e)}
          name={'Description'}
          withAsterisk
          disabled={!canEdit}
        />

        <SCDropdownList
          name="LocationType"
          options={Enums.getEnumItems(Enums.LocationType)}
          label="Address Type"
          required={true}
          {...form.getInputProps('Type')}
          onChange={changeType}
          // Shows the form's type only: a change can be cancelled (see changeType)
          suppressInternalValueChange
          value={form.values.Type}
          disabled={!canEdit}
        />

        {/* The address gets a row of its own */}
        <Box style={{ gridColumn: '1 / -1' }}>
          <Textarea
            mt={'sm'}
            // The theme caps inputs at 500px; the address spans the full row
            styles={{ root: { maxWidth: 'none' } }}
            label={'Address'}
            value={addressText}
            onChange={(e: any) => {
              const split = e.currentTarget.value.split('\n').slice(0, 5);
              form.setFieldValue('AddressLine1', split[0] || '');
              form.setFieldValue('AddressLine2', split[1] || '');
              form.setFieldValue('AddressLine3', split[2] || '');
              form.setFieldValue('AddressLine4', split[3] || '');
              form.setFieldValue('AddressLine5', split[4] || '');
              setAddressText(split.join('\n'));
            }}
            error={
              form.errors?.AddressLine1 ||
              form.errors?.AddressLine2 ||
              form.errors?.AddressLine3 ||
              form.errors?.AddressLine4 ||
              form.errors?.AddressLine5
            }
            maxRows={5}
            minRows={4}
            autosize
            withAsterisk
            disabled={!canEdit}
          />
        </Box>

        <Box>
          <SCDropdownList
            name="Country"
            textField="Description"
            dataItemKey="ID"
            options={countries}
            label="Country"
            required={true}
            {...form.getInputProps('Country')}
            onChange={(x: any) => {
              form.setFieldValue('Country', x);
              form.setFieldValue('CountryID', x?.ID);
              form.setFieldValue('CountryDescription', x?.Description);
            }}
            value={form.values.Country}
            disabled={!canEdit}
          />
        </Box>
      </SimpleGrid>

      <Checkbox
        mt={'lg'}
        label={'Use latitude / longitude'}
        description={"Pin the exact spot when the address isn't precise enough, e.g. a farm or site."}
        checked={!!useCoords}
        onChange={(e) => {
          const checked = e.currentTarget.checked;
          setUseCoords(checked);
          if (!checked) {
            form.setFieldValue('Latitude', null);
            form.setFieldValue('Longitude', null);
          }
        }}
        disabled={!canEdit}
      />
      <LocationLatLngControl
        addressText={addressText}
        countryName={form.values.CountryDescription}
        initialLatitude={initialValues.Latitude as any}
        initialLongitude={initialValues.Longitude as any}
        disabled={!canEdit}
        useCoords={useCoords}
        onChange={({ latitude, longitude }: any) => {
          form.setFieldValue('Latitude', latitude);
          form.setFieldValue('Longitude', longitude);
        }}
      />

      <Box mt={'xl'}>
        <Text fz={14} fw={600} c={'gray.9'}>Role for this customer</Text>
        <Tooltip
          openDelay={300}
          label={primaryTooltip}
          color={'scBlue'}
          multiline
          w={280}
          disabled={!primaryTooltip}
          position={'top-start'}
        >
          <Box w={'fit-content'}>
            <Checkbox
              mt={'sm'}
              label={`Primary ${typeName} address`}
              checked={primaryChecked}
              onChange={(e) => {
                const checked = e.currentTarget.checked;
                if (typeRulesApply) togglePrimary(checked);
                else if (!alwaysChecked) {
                  form.setFieldValue('IsPrimary', checked);
                  if (checked) form.setFieldValue('IsActive', true);
                }
              }}
              color={!typeRulesApply && defaultPrimaryToggleColor ? defaultPrimaryToggleColor : undefined}
              disabled={primaryDisabled}
            />
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  if (!isModalMode) {
    // Embedded usage: just return the content
    return formContent;
  }

  // Modal mode (replacement for manage-location)
  return (
    <SCModal open={true} decor={'none'} onClose={() => {}} size={'lg'}>
      <div>
        {backButtonText && (
          <Group style={{ borderBottom: '1px solid var(--mantine-color-gray-1)', paddingBottom: 'var(--mantine-spacing-md)' }}>
            <Button variant={'subtle'} color={'gray.9'} onClick={onCancel}>
              {backButtonText}
            </Button>
          </Group>
        )}

        <Group my={'var(--mantine-spacing-lg)'} justify={'space-between'} align={'center'} wrap={'nowrap'} gap={'sm'}>
          <Title size={'md'} fw={600}>
            {isNew ? 'Add new location' : `Editing location ${form.values.Description ?? ''}`}
          </Title>
          {statusPill}
        </Group>

        {formContent}

        <Group mt={'xl'} justify={'space-between'} gap={'xs'}>
          <div>
            {canChangeActiveStatus && (
              <Button
                type={'button'}
                variant={'outline'}
                color={location?.IsActive ? 'red' : 'scBlue'}
                disabled={saving || changingActiveStatus || isAccessLocked}
                rightSection={changingActiveStatus && !deactivateCandidates && <Loader variant={'oval'} size={18} />}
                onClick={location?.IsActive ? startDeactivate : activate}
              >
                {location?.IsActive ? 'Deactivate' : 'Activate'}
              </Button>
            )}
          </div>
          <Group gap={'xs'}>
            <Button type={'button'} variant={'subtle'} color={'gray.9'} onClick={onCancel}>
              Cancel
            </Button>
            {canEdit && (
              <Button
                color={'scBlue'}
                disabled={saving || changingActiveStatus || isAccessLocked}
                rightSection={saving && <Loader variant={'oval'} size={18} color={'white'} />}
                onClick={saveLocation}
              >
                {isNew ? 'Create' : 'Save'}
              </Button>
            )}
          </Group>
        </Group>
      </div>

      {replacing && location && (
        <LocationPrimaryReplacementModal
          location={location}
          typeName={loadedTypeName}
          newTypeName={replacing.typeValue ? String(replacing.typeValue).toLowerCase() : undefined}
          candidates={othersOfType(loadedType as number)}
          onConfirm={confirmReplacement}
          onCancel={() => setReplacing(null)}
        />
      )}

      {deactivateCandidates && location && (
        <LocationDeactivateModal
          location={location}
          locationTypeName={locationTypeName}
          candidates={deactivateCandidates}
          hasUnsavedChanges={hasUnsavedChanges}
          saving={changingActiveStatus}
          onConfirm={confirmDeactivate}
          onCancel={() => setDeactivateCandidates(null)}
        />
      )}
    </SCModal>
  );
}

export default LocationForm;
